import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Button from "@mui/material/Button";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useESRApprovalContext } from "../../Context/ESRApprovalContext";
import { appColors, appStyles } from "../../../../../../theme";
import CloseButton from "./ui/CloseButton";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CustomInput from "./ui/CustomInput";
import { useGetPartsQuery } from "../../../../../../hooks/useParts";
import { EmailContents, EmailPartsList, HeSvForEmail } from "./utils/types";
import LoadingSlider from "./ui/LoadingSlider";
import {
  EmailContentsInitialValues,
  EmailContentsSchema,
} from "./utils/schema";
import { yupResolver } from "@hookform/resolvers/yup";
import { IParts } from "../../../../../DatabaseManagement/ServicingDBs/Parts/utils/parts-utils";
import { useSnackBar } from "../../../../../../components/ToastProvider";
import { getFullDate } from "../../../../../../utils/helpers";
import { useSendEmailPartsList } from "../../../../../../hooks/useESRApproval";
import AppDialog from "../../../../../../components/common/AppDialog";
import MultiEmailForm from "../../../../../../components/common/MultiEmailForm";
import AppTextField from "../../../../../../components/common/forms/AppTextField";

interface EmailSendingProp {
  esrNumber: string;
  equipmentNo: string;
}

const EmailSendingForm = ({ esrNumber, equipmentNo }: EmailSendingProp) => {
  const { showSnackBar } = useSnackBar();
  const sendEmail = useSendEmailPartsList();
  const { data: heSvParts } = useGetPartsQuery();
  const { esrEquipmentServicesArray } = useESRApprovalContext();
  const [newTo, setNewTo] = useState<string>("");
  const [newCc, setNewCc] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const { setOpenEmailSendingDialog } = useESRApprovalContext();

  const fileName = `${esrNumber}_task_partnumber.csv`;

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<EmailContents>({
    defaultValues: EmailContentsInitialValues,
    mode: "onChange",
    resolver: yupResolver(EmailContentsSchema),
  });

  const getHeSvPartsArray = (): HeSvForEmail[] => {
    if (!heSvParts || esrEquipmentServicesArray.length === 0) {
      return [];
    }

    if (!heSvParts) {
      return [];
    }

    // Create a map for quick lookup of heSvParts by partNumber
    const heSvPartsMap = new Map<string, IParts>(
      heSvParts.map((heSvPart) => [heSvPart.partNumber, heSvPart])
    );

    // Iterate through esrEquipmentServicesArray and fill heSvPartsArray
    return esrEquipmentServicesArray.map((equipmentService) => {
      const foundHeSvPart = heSvPartsMap.get(equipmentService.partNumber);
      return foundHeSvPart
        ? {
            description: String(foundHeSvPart.description),
            make: String(foundHeSvPart.make),
            supplier: foundHeSvPart.supplier ?? "No supplier",
          }
        : {
            description: "",
            make: "",
            supplier: "",
          };
    });
  };

  const attachPartsListCsv = () => {
    const heSvParts = getHeSvPartsArray();
    const header = [
      "ESR No.",
      "Equipment No.",
      "Part No.",
      "Task Type",
      "Description",
      "Quantity",
      "Make",
      "Supplier",
    ];

    const formattedPartsLists: EmailPartsList[] = esrEquipmentServicesArray.map(
      (equipmentService, index) => {
        return {
          esrNumber: esrNumber,
          equipmentNo: equipmentNo,
          partNumber: equipmentService.partNumber,
          taskType: equipmentService.taskTypeItem,
          quantity: equipmentService.quantity,
          ...heSvParts[index],
        };
      }
    );

    const formattedArraysToCsvString = [
      header,
      ...formattedPartsLists.map((heSvPart) => [
        heSvPart.esrNumber,
        heSvPart.equipmentNo,
        heSvPart.partNumber,
        heSvPart.taskType,
        heSvPart.description,
        heSvPart.quantity,
        heSvPart.make,
        heSvPart.supplier,
      ]),
    ]
      .map((e) => e.join(","))
      .join("\n");

    return formattedArraysToCsvString;
  };

  const attachFile = async () => {
    setIsLoading(true);

    try {
      await new Promise((resolve) => {
        setTimeout(() => {
          setValue("attachments", {
            filename: fileName,
            content: attachPartsListCsv(),
          });
          resolve(null);
        }, 2000);
      });
    } catch (e) {
      showSnackBar("Attaching failed, please retry!", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = (formData: EmailContents) => {
    if (watch("to").length == 0) {
      showSnackBar(`Please add a valid recipient.`, "warning");
      return;
    }
    if (newTo || newCc) {
      if (newTo != " " && newCc != " ") {
        showSnackBar(
          `Please ensure all recipients are added or removed before sending the email.`,
          "warning"
        );
        return;
      }
    }
    const emailSubject = `${esrNumber}_${equipmentNo}_${watch(
      "subject"
    )}_${getFullDate(new Date())}`;
    const formattedEmail: EmailContents = {
      to: formData.to,
      cc: formData.cc,
      message: formData.message,
      subject: emailSubject,
      attachments: formData.attachments,
    };
    sendEmail.mutate(formattedEmail);
  };

  return (
    <AppDialog
      open
      onDialogClose={() => setOpenEmailSendingDialog(false)}
      title="Email Parts List"
      maxWidth="lg"
    >
      <Stack
        component="form"
        height="100%"
        bgcolor="white"
        py={3}
        borderRadius={1}
      >
        <Stack px={3} height="100%" overflow="auto" gap={2}>
          <Controller
            control={control}
            name="to"
            render={({ field: { onChange, value } }) => (
              <MultiEmailForm
                value={value}
                setValue={onChange}
                newEmailAddress={newTo}
                setNewEmailAddress={setNewTo}
                id="to"
                label="To:"
                placeholder="Type here"
                isEmail={true}
              />
            )}
          />

          <Controller
            control={control}
            name="cc"
            render={({ field: { onChange, value } }) => (
              <MultiEmailForm
                value={value}
                setValue={onChange}
                newEmailAddress={newCc}
                setNewEmailAddress={setNewCc}
                id="cc"
                label="Cc:"
                placeholder="Type here"
                isEmail={true}
              />
            )}
          />

          <AppTextField
            label="Subject"
            required={false}
            id="subject"
            isMultiline={true}
            sx={{
              width: "100%",
              alignItems: "start",
              mt: 0.75,
            }}
            helperText={
              errors.subject && (
                <span style={{ color: "#DD4C39", marginLeft: "-14px" }}>
                  Subject is required
                </span>
              )
            }
            {...register("subject")}
          />
          <AppTextField
            id="message"
            label="Message"
            required={false}
            isMultiline={true}
            multiline
            sx={{
              height: 300,
              width: "100%",
              alignItems: "start",
              mt: 0.75,
            }}
            helperText={
              errors.message && (
                <span style={{ color: "#DD4C39", marginLeft: "-14px" }}>
                  Message is required
                </span>
              )
            }
            {...register("message")}
          />
          <Stack direction="row" gap={1} sx={{ marginLeft: "auto" }}>
            {watch("attachments").content ? (
              <Chip
                variant="outlined"
                label={fileName}
                sx={{ marginTop: "4px" }}
                onDelete={() =>
                  setValue("attachments", {
                    filename: "",
                    content: "",
                  })
                }
              />
            ) : (
              <Button
                disabled={
                  !!watch("attachments").content ||
                  sendEmail.isLoading ||
                  sendEmail.isSuccess ||
                  isLoading
                }
                onClick={() => {
                  attachFile();
                }}
              >
                {!isLoading ? (
                  <>
                    <AttachFileIcon
                      sx={{ fontSize: "16px", marginRight: "4px" }}
                    />
                    Attach Parts List
                  </>
                ) : (
                  "Attaching..."
                )}
              </Button>
            )}
            <Button
              onClick={handleSubmit(onSubmit)}
              disabled={
                !errors ||
                sendEmail.isLoading ||
                sendEmail.isSuccess ||
                isLoading
              }
              sx={{ width: 150 }}
            >
              {sendEmail.isLoading ? "Sending..." : "Send"}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </AppDialog>
  );
};

export default EmailSendingForm;
